/**
 * sessionStorage persistence for the resumable, idempotent chunked commit (D5).
 *
 * Each import records its per-chunk progress — the deterministic run id, the
 * 0-based indices of chunks that have committed server-side, and the running
 * aggregate committed/failed counts — keyed by `(companyCode, mypayEnvironment,
 * file signature)`.
 * If the tab is closed or the commit is interrupted mid-run, a later commit of
 * the *same* file for the *same* company can load this record, skip the
 * already-committed chunks (their `correlation_id`s would replay idempotently
 * anyway), and fold the prior counts back into the aggregate so the results
 * screen never shows a misleading "0 committed".
 *
 * The record is best-effort: storage may be full, disabled, or unavailable
 * (SSR / hardened browser). Every access is guarded so a storage failure can
 * never break the commit itself — the worst case is that resume is skipped.
 */

import { z } from "zod";
import { fileSignature } from "./commitCorrelation";

/** sessionStorage key namespace for resumable commit progress. */
const STORAGE_PREFIX = "pci-commit-progress";

/**
 * Persisted per-chunk commit progress for one `(companyCode, file)` import.
 */
export interface CommitProgressRecord {
  /** Unique per-import-attempt run id (see `createRunId`). */
  runId: string;
  /** 0-based indices of chunks whose commit has completed server-side. */
  committedChunks: number[];
  /** Aggregate committed row count across the committed chunks. */
  committed: number;
  /** Aggregate failed row count across the committed chunks. */
  failed: number;
}

const CommitProgressRecordSchema = z.object({
  runId: z.string(),
  committedChunks: z.array(z.number()),
  committed: z.number(),
  failed: z.number(),
});

/** Wizard field not chosen yet (falls back to reserved header at resolve time). */
const SHAPE_UNSET = "\u0001unset";
/** Explicit "— none —" (skip dates even when a reserved header exists). */
const SHAPE_NONE = "\u0001none";

/**
 * Encode a wizard column pick so unset (`null`) and none (`""`) stay distinct
 * in the resume key — they resolve differently in `resolveDateColumnHeader`.
 */
function encodeShapePart(value: string | null | undefined): string {
  if (value === undefined || value === null) return SHAPE_UNSET;
  if (value === "") return SHAPE_NONE;
  return value;
}

/**
 * Fingerprint of import-shaping wizard options that change how rows are built
 * from the same CSV bytes (date/component column picks). Included in the
 * resume key so changing mappings cannot resume against stale chunks.
 */
export function importShapeKey(args: {
  componentColumn?: string | null;
  startDateColumn?: string | null;
  endDateColumn?: string | null;
}): string {
  const parts = [
    encodeShapePart(args.componentColumn),
    encodeShapePart(args.startDateColumn),
    encodeShapePart(args.endDateColumn),
  ];
  // All unset → same resume key as pre-shape commits (empty suffix).
  if (parts.every((p) => p === SHAPE_UNSET)) return "";
  return parts.join("\0");
}

/**
 * True when date columns were never chosen in the wizard (`null`/`undefined`).
 * Used to fall back to pre-shape (unshaped) progress keys after deploy.
 */
export function dateColumnsStillUnset(args: {
  startDateColumn?: string | null;
  endDateColumn?: string | null;
}): boolean {
  return (
    (args.startDateColumn === undefined || args.startDateColumn === null) &&
    (args.endDateColumn === undefined || args.endDateColumn === null)
  );
}

/** sessionStorage key for a given company + environment + file + import shape. */
function progressKey(
  companyCode: string,
  mypayEnvironment: string,
  csvText: string,
  shapeKey: string = "",
): string {
  const shape = shapeKey ? `:${fileSignature(shapeKey)}` : "";
  return `${STORAGE_PREFIX}:${companyCode}:${mypayEnvironment}:${fileSignature(csvText)}${shape}`;
}

/**
 * Resolve sessionStorage, returning `null` when it is unavailable (SSR, or a
 * browser that throws on access in private mode). Callers treat `null` as
 * "no persistence" and degrade to a non-resumable commit.
 */
function getStore(): Storage | null {
  try {
    return typeof sessionStorage !== "undefined" ? sessionStorage : null;
  } catch {
    return null;
  }
}

/**
 * Persist (or overwrite) the commit progress for this company + file. A no-op
 * when storage is unavailable or full.
 */
export function saveCommitProgress(
  companyCode: string,
  mypayEnvironment: string,
  csvText: string,
  record: CommitProgressRecord,
  shapeKey: string = "",
): void {
  const store = getStore();
  if (!store) return;
  try {
    store.setItem(
      progressKey(companyCode, mypayEnvironment, csvText, shapeKey),
      JSON.stringify(record),
    );
  } catch {
    // Storage full or disabled — resume is best-effort, so swallow.
  }
}

/**
 * Load saved commit progress for this company + environment + file, or `null`
 * when there is none (or the stored value is missing/corrupt/stale-shaped).
 */
export function loadCommitProgress(
  companyCode: string,
  mypayEnvironment: string,
  csvText: string,
  shapeKey: string = "",
): CommitProgressRecord | null {
  const store = getStore();
  if (!store) return null;
  let raw: string | null;
  try {
    raw = store.getItem(
      progressKey(companyCode, mypayEnvironment, csvText, shapeKey),
    );
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    return CommitProgressRecordSchema.parse(JSON.parse(raw));
  } catch {
    // Corrupt JSON or an incompatible older shape — treat as no saved progress.
    return null;
  }
}

/**
 * Load progress under `shapeKey`, with a one-shot migration from pre-shape keys.
 *
 * Before import-shape suffixes, progress lived under the unshaped key. After
 * deploy, a restored session usually has a nonempty `componentColumn`, so the
 * shaped lookup would miss and mint a new run id. When date columns are still
 * unset (same effective dates as pre-feature), fall back to the unshaped record
 * and migrate it under the shaped key.
 */
export function loadCommitProgressResumable(
  companyCode: string,
  mypayEnvironment: string,
  csvText: string,
  shapeKey: string,
  opts: { allowUnshapedFallback: boolean },
): CommitProgressRecord | null {
  const shaped = loadCommitProgress(
    companyCode,
    mypayEnvironment,
    csvText,
    shapeKey,
  );
  if (shaped || !opts.allowUnshapedFallback || shapeKey === "") {
    return shaped;
  }
  const legacy = loadCommitProgress(companyCode, mypayEnvironment, csvText, "");
  if (!legacy) return null;
  // Migrate so subsequent chunk saves / clears use the shaped key.
  saveCommitProgress(
    companyCode,
    mypayEnvironment,
    csvText,
    legacy,
    shapeKey,
  );
  clearCommitProgress(companyCode, mypayEnvironment, csvText, "");
  return legacy;
}

/**
 * Remove any saved commit progress for this company + environment + file. Called
 * on a clean full completion or when a fresh file starts, so a completed import
 * can never be mistaken for a resumable one.
 */
export function clearCommitProgress(
  companyCode: string,
  mypayEnvironment: string,
  csvText: string,
  shapeKey: string = "",
): void {
  const store = getStore();
  if (!store) return;
  try {
    store.removeItem(
      progressKey(companyCode, mypayEnvironment, csvText, shapeKey),
    );
  } catch {
    // Nothing actionable if removal fails; a stale entry is harmless.
  }
}
